import React, { useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Portal, Surface, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import JobsContext from '../context/JobsContext';
import { Palette, radius, spacing, useThemePalette } from '../theme';

// The OK button stays disabled this long on first launch so the user is nudged
// to actually read the notice instead of dismissing it reflexively.
const COUNTDOWN_SECONDS = 3;

// First-launch notice clarifying that job listings come from third-party APIs
// and are not curated or published by us. Shown once (gated on the persisted
// `disclaimerAcknowledged` flag) as a blocking overlay on every platform.
const DisclaimerModal: React.FC = () => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { hydrated, disclaimerAcknowledged, acknowledgeDisclaimer } = useContext(JobsContext);
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);

  // Only show after hydration so the persisted flag is known (no flash on a
  // returning user who already dismissed it).
  const visible = hydrated && !disclaimerAcknowledged;

  useEffect(() => {
    if (!visible) return;
    setRemaining(COUNTDOWN_SECONDS);
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  const disabled = remaining > 0;

  return (
    <Portal>
      <View style={styles.backdrop}>
        <Surface style={styles.card} elevation={4}>
          <Text style={styles.title}>{t('disclaimer.title')}</Text>
          <Text style={styles.message}>{t('disclaimer.message')}</Text>
          <Button
            mode="contained"
            testID="disclaimer-ok"
            disabled={disabled}
            onPress={acknowledgeDisclaimer}
            style={styles.button}
          >
            {disabled ? t('disclaimer.okCountdown', { seconds: remaining }) : t('common.ok')}
          </Button>
        </Surface>
      </View>
    </Portal>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.lg,
    },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: p.border,
      padding: spacing.lg,
      width: '100%',
      maxWidth: 460,
      gap: spacing.md,
    },
    title: {
      color: p.textPrimary,
      fontSize: 18,
      fontWeight: '700',
    },
    message: {
      color: p.textSecondary,
      fontSize: 14,
      lineHeight: 21,
    },
    button: {
      marginTop: spacing.sm,
      alignSelf: 'flex-end',
    },
  });

export default DisclaimerModal;
